import { useCallback, useState } from 'react'
import { GridApi } from 'ag-grid-community'
import {
  CriticalPathLinkData,
  CriticalPathLinkUpdates,
  useCriticalPathContext,
} from './CriticalPathContext'
import {
  getWbsItemDisplayName,
  getWbsItemIconUrl,
  getWbsItemScheduledDate,
} from '../utils/linkInfoPopupUtils'
import { LinkEditorResult } from '../WbsItemLink/LinkEditorPopup'

export interface CpPopupState {
  link: CriticalPathLinkData
  fromName: string
  toName: string
  fromIconUrl?: string
  toIconUrl?: string
  fromDateRange: { min: Date; max: Date } | null
  toDateRange: { min: Date; max: Date } | null
}

const toDateRange = (
  scheduled: { startDate?: string; endDate?: string } | undefined
): { min: Date; max: Date } | null => {
  if (!scheduled?.startDate || !scheduled?.endDate) return null
  return {
    min: new Date(scheduled.startDate),
    max: new Date(scheduled.endDate),
  }
}

export const useCriticalPathLinePopup = (
  api: GridApi | undefined,
  dataPath?: string
) => {
  const criticalPathContext = useCriticalPathContext()
  const [cpPopup, setCpPopup] = useState<CpPopupState | null>(null)

  const handleLineClick = useCallback(
    (link: CriticalPathLinkData, event: React.MouseEvent) => {
      const prefix = dataPath ? `${dataPath}.` : ''
      const uuidMap = criticalPathContext?.wbsItemUuidToRowIndex
      const fromRow = uuidMap?.get(link.fromWbsItemUuid) ?? link.fromRowIndex
      const toRow = uuidMap?.get(link.toWbsItemUuid) ?? link.toRowIndex
      const fromName = api ? getWbsItemDisplayName(api, fromRow, prefix) : ''
      const toName = api ? getWbsItemDisplayName(api, toRow, prefix) : ''

      const fromScheduled = api
        ? getWbsItemScheduledDate(api, fromRow, prefix)
        : undefined
      const toScheduled = api
        ? getWbsItemScheduledDate(api, toRow, prefix)
        : undefined

      const fromIconUrl = api
        ? getWbsItemIconUrl(api, fromRow, prefix)
        : undefined
      const toIconUrl = api ? getWbsItemIconUrl(api, toRow, prefix) : undefined
      setCpPopup({
        link,
        fromName,
        toName,
        fromIconUrl,
        toIconUrl,
        fromDateRange: toDateRange(fromScheduled),
        toDateRange: toDateRange(toScheduled),
      })
    },
    [api, dataPath, criticalPathContext]
  )

  const handleDelete = useCallback(() => {
    if (cpPopup && criticalPathContext) {
      criticalPathContext.removeCriticalPathLink(cpPopup.link.uuid)
    }
    setCpPopup(null)
  }, [cpPopup, criticalPathContext])

  const handleUpdate = useCallback(
    (result: LinkEditorResult) => {
      if (!cpPopup || !criticalPathContext) return

      // Apply date changes
      if (result.fromDate) {
        criticalPathContext.updateCriticalPathLinkDate(
          cpPopup.link.uuid,
          'fromDate',
          result.fromDate
        )
      }
      if (result.toDate) {
        criticalPathContext.updateCriticalPathLinkDate(
          cpPopup.link.uuid,
          'toDate',
          result.toDate
        )
      }

      // Apply color/description changes
      const propUpdates: CriticalPathLinkUpdates = {}
      if (result.color !== undefined) propUpdates.color = result.color
      if (result.description !== undefined) {
        propUpdates.description = result.description
      }
      if (Object.keys(propUpdates).length > 0) {
        criticalPathContext.updateCriticalPathLinkProps(
          cpPopup.link.uuid,
          propUpdates
        )
      }

      setCpPopup(null)
    },
    [cpPopup, criticalPathContext]
  )

  const closePopup = useCallback(() => setCpPopup(null), [])

  // Open popup with pre-resolved info (for fallback links where one endpoint is hidden)
  const openPopup = useCallback((state: CpPopupState) => setCpPopup(state), [])

  return {
    cpPopup,
    handleLineClick,
    handleDelete,
    handleUpdate,
    closePopup,
    openPopup,
  }
}
